import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import MangaCard from "@/components/feature/MangaCard";
import { mangaList, genreList } from "@/mocks/manga";

export default function MangaList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeGenre = searchParams.get("genre") || "";
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 9;

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return mangaList.filter((m) => {
      const genreMatch = !activeGenre || m.genre === activeGenre || m.genres.includes(activeGenre);
      const kwMatch = !kw || m.title.toLowerCase().includes(kw) || m.author.toLowerCase().includes(kw);
      return genreMatch && kwMatch;
    });
  }, [activeGenre, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const setGenre = (g: string) => {
    setPage(1);
    setSearchParams(g ? { genre: g } : {});
  };

  return (
    <>
      <Seo
        title={`${activeGenre ? `${activeGenre}漫画推荐` : "漫画库"} - 樱花漫画`}
        description="按题材分类浏览优质国产漫画，支持热血冒险、治愈美食、青春恋爱、国风志怪等分类，快速找到你想追的作品。"
        canonical="https://comicflower.github.io/manga"
      />
      <div className="mx-auto max-w-7xl px-4 pt-24 md:px-6 md:pt-28">
        <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-800">漫画库</span>
        </nav>

        <div className="mt-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-heading text-3xl font-black text-foreground-950">
              {activeGenre ? activeGenre : "全部漫画"}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              共收录 <span className="font-medium text-foreground-900">{filtered.length}</span> 部作品，按题材筛选你喜欢的漫画。
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-sm text-foreground-500" aria-hidden="true"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              placeholder="搜索漫画或作者"
              className="w-full rounded-full border border-background-300 bg-background-100 py-2.5 pl-11 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setGenre("")}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
              activeGenre === ""
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            全部
          </button>
          {genreList.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGenre(g)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                activeGenre === g
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        {paged.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {paged.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-background-200 bg-background-100 py-20 text-center">
            <i className="ri-inbox-archive-line text-4xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的作品，换个题材或关键词试试吧。</p>
            <button
              type="button"
              onClick={() => {
                setGenre("");
                setKeyword("");
              }}
              className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              清除筛选
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage(safePage - 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-700 disabled:opacity-40"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`h-10 w-10 rounded-full text-sm font-medium ${
                  p === safePage
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage(safePage + 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-background-100 text-foreground-700 disabled:opacity-40"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </div>
    </>
  );
}